import { ImageIcon, VideoIcon } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useInfiniteListAssets } from '@/features/assets';
import { useInfiniteListGraphics } from '@/features/graphics';
import { useListVideos } from '@/features/videos';
import { cn } from '@/lib/utils';
import { type MediaSource, MediaSourceSelect } from './media-source-select';

/**
 * What the picker hands back. `videoId` carries either a rendered `video.id`
 * or an uploaded `asset.id` — the ad row's `videoId` column and the replace
 * endpoint both accept either, exactly as the ad wizard's media step passes
 * them (`select-video-step.tsx` sets `videoId` from all three sources).
 */
export interface PickedCreative {
  videoId?: string;
  graphicId?: string;
  /** Poster/still for the staged preview, so the panel can show the choice. */
  thumbnailUrl?: string;
  /** Playable source, when the pick is a video. */
  videoUrl?: string;
  label: string;
}

interface CreativePickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (creative: PickedCreative) => void;
}

type MediaTab = 'videos' | 'images';

interface Tile {
  key: string;
  source: Exclude<MediaSource, 'all'>;
  label: string;
  thumbnailUrl?: string;
  picked: PickedCreative;
}

function TileGrid({
  tiles,
  isLoading,
  emptyLabel,
  selectedKey,
  onSelect,
}: {
  tiles: Tile[];
  isLoading: boolean;
  emptyLabel: string;
  selectedKey: string | null;
  onSelect: (tile: Tile) => void;
}) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-3 gap-3">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="aspect-square w-full rounded-md" />
        ))}
      </div>
    );
  }

  if (tiles.length === 0) {
    return (
      <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
        {emptyLabel}
      </p>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-3">
      {tiles.map((tile) => (
        <button
          key={tile.key}
          type="button"
          onClick={() => onSelect(tile)}
          aria-pressed={selectedKey === tile.key}
          className={cn(
            'group relative overflow-hidden rounded-md border bg-muted text-left transition',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            selectedKey === tile.key
              ? 'ring-2 ring-primary'
              : 'hover:border-foreground/30'
          )}
        >
          <div className="aspect-square w-full">
            {tile.thumbnailUrl ? (
              <img
                src={tile.thumbnailUrl}
                alt={tile.label}
                className="size-full object-cover"
              />
            ) : tile.picked.videoUrl ? (
              <video
                src={`${tile.picked.videoUrl}#t=0.1`}
                className="size-full object-cover"
                muted
                playsInline
                preload="metadata"
              />
            ) : (
              <div className="flex size-full items-center justify-center text-muted-foreground">
                <ImageIcon className="size-6" />
              </div>
            )}
          </div>
          <span className="block truncate px-2 py-1.5 text-xs">
            {tile.label}
          </span>
        </button>
      ))}
    </div>
  );
}

/**
 * Pick a new creative for an ad from the org's own media.
 *
 * Read-only over the existing media lists. It uploads nothing and renders
 * nothing new, so there is no way for it to leave a half-made asset behind.
 * Choosing here only STAGES the creative; the ad is not touched until the
 * panel's Save.
 */
export function CreativePickerDialog({
  open,
  onOpenChange,
  onPick,
}: CreativePickerDialogProps) {
  const [tab, setTab] = useState<MediaTab>('videos');
  const [source, setSource] = useState<MediaSource>('all');
  const [selected, setSelected] = useState<Tile | null>(null);

  const videosQuery = useListVideos();
  const { videos, isLoading: videosLoading } = videosQuery;
  const videoAssetsQuery = useInfiniteListAssets({ type: 'video' });
  const imageAssetsQuery = useInfiniteListAssets({
    type: 'image',
  });
  const graphicsQuery = useInfiniteListGraphics();
  const { assets: imageAssets, isLoading: imagesLoading } = imageAssetsQuery;
  const { graphics, isLoading: graphicsLoading } = graphicsQuery;
  const mediaQueries =
    tab === 'videos'
      ? source === 'generated'
        ? []
        : [videoAssetsQuery]
      : source === 'all'
        ? [imageAssetsQuery, graphicsQuery]
        : source === 'uploaded'
          ? [imageAssetsQuery]
          : [graphicsQuery];
  const moreQueries = mediaQueries.filter((query) => query.hasNextPage);
  const failedQueries = [
    ...mediaQueries,
    ...(tab === 'videos' && source !== 'uploaded' ? [videosQuery] : []),
  ].filter((query) => query.isError);

  // Only media that is actually renderable can become a creative: a video
  // still encoding has no playable source, and a graphic mid-render has no
  // output to point Meta at.
  const videoTiles: Tile[] = [
    ...videoAssetsQuery.assets.map((a) => ({
      key: `asset:${a.id}`,
      source: 'uploaded' as const,
      label: a.name ?? 'Uploaded video',
      thumbnailUrl: a.thumbnailUrl ?? undefined,
      picked: {
        videoId: a.id,
        thumbnailUrl: a.thumbnailUrl ?? undefined,
        videoUrl: a.blobUrl,
        label: a.name ?? 'Uploaded video',
      },
    })),
    ...videos
      .filter((v) => v.status === 'ready')
      .map((v) => ({
        key: `video:${v.id}`,
        source: 'generated' as const,
        label: v.title ?? 'Untitled video',
        thumbnailUrl: v.thumbnailUrl ?? undefined,
        picked: {
          videoId: v.id,
          thumbnailUrl: v.thumbnailUrl ?? undefined,
          videoUrl: v.blobUrl ?? undefined,
          label: v.title ?? 'Untitled video',
        },
      })),
  ];

  const imageTiles: Tile[] = [
    ...imageAssets.map((a) => ({
      key: `asset:${a.id}`,
      source: 'uploaded' as const,
      label: a.name ?? 'Uploaded image',
      thumbnailUrl: a.thumbnailUrl ?? a.blobUrl ?? undefined,
      picked: {
        videoId: a.id,
        thumbnailUrl: a.thumbnailUrl ?? a.blobUrl ?? undefined,
        label: a.name ?? 'Uploaded image',
      },
    })),
    ...graphics
      .filter((g) => g.status === 'ready')
      .map((g) => {
        const url = g.outputs?.find((o) => o.status !== 'failed')?.url;
        return {
          key: `graphic:${g.id}`,
          source: 'generated' as const,
          label: g.title ?? 'Graphic',
          thumbnailUrl: url,
          picked: {
            graphicId: g.id,
            thumbnailUrl: url,
            label: g.title ?? 'Graphic',
          },
        };
      }),
  ];

  const visibleTiles = (tab === 'videos' ? videoTiles : imageTiles).filter(
    (tile) => source === 'all' || tile.source === source
  );
  const isLoading =
    tab === 'videos'
      ? (source !== 'uploaded' && videosLoading) ||
        (source !== 'generated' && videoAssetsQuery.isLoading)
      : (source !== 'generated' && imagesLoading) ||
        (source !== 'uploaded' && graphicsLoading);

  const handleConfirm = () => {
    if (!selected) return;
    onPick(selected.picked);
    setSelected(null);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Choose a new creative</DialogTitle>
          <DialogDescription>
            Pick from your videos and images. Nothing changes on the ad until
            you save.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center justify-between gap-2">
          <Tabs value={tab} onValueChange={(v) => setTab(v as MediaTab)}>
            <TabsList>
              <TabsTrigger value="videos">
                <VideoIcon className="size-4" />
                Videos
              </TabsTrigger>
              <TabsTrigger value="images" aria-label="Images & graphics">
                <ImageIcon className="size-4" />
                <span>
                  Images<span className="hidden sm:inline"> & graphics</span>
                </span>
              </TabsTrigger>
            </TabsList>
          </Tabs>
          <MediaSourceSelect value={source} onValueChange={setSource} />
        </div>

        <div className="max-h-[45vh] overflow-y-auto">
          {(isLoading ||
            visibleTiles.length > 0 ||
            failedQueries.length === 0) && (
            <TileGrid
              tiles={visibleTiles}
              isLoading={isLoading}
              emptyLabel={
                tab === 'videos'
                  ? 'No videos yet. Upload or create a video to use it here.'
                  : 'No images yet.'
              }
              selectedKey={selected?.key ?? null}
              onSelect={setSelected}
            />
          )}
          {failedQueries.length > 0 && (
            <div role="alert" className="mt-3 text-sm text-destructive">
              <p>Some media could not be loaded.</p>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  for (const query of failedQueries) void query.refetch();
                }}
              >
                Try again
              </Button>
            </div>
          )}
          {moreQueries.length > 0 && (
            <Button
              type="button"
              variant="outline"
              className="mt-3 w-full"
              disabled={moreQueries.some((query) => query.isFetchingNextPage)}
              onClick={() => {
                for (const query of moreQueries) void query.fetchNextPage();
              }}
            >
              Load more
            </Button>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!selected}>
            Use this creative
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
